import { useState, useEffect, type CSSProperties, type ReactNode } from 'react';
import { Container, Group, NakedButton as PennyNakedButton, Text } from '@melio/penny';

import { useApp, Banner, Mono, NakedButton, Button, Card, Pill, Icon, Input, Field } from '@/components/ds';
import { Method, CodeBlock, Toggle } from '@/components/code';
import { jsonStr } from '@/lib/codegen';
import { RES, DASH } from '@/data';
import { PageTitle, Table, Drawer } from '@/containers/dashboard';
import { DefList } from '@/containers/dashboard-businesses';
import { ApiKeyPanel } from '@/containers/onboarding';
import { PreviewNote, PreviewBadge, useApiCtx, useAsync, Loading, ErrorBanner } from '@/components/live';
import { listKeys, revealKey, rotateKey, expireKey, type ApiKeyMeta, type OnboardEnv } from '@/api/portal';
import { getWebhook, patchWebhook, deleteWebhook, listWebhookEvents, type Webhook, type WebhookEventName } from '@/api/payouts';

// dashboard-developers.tsx — Developers: API keys, Webhooks (both live), plus Logs & Events (preview).
// API keys are managed through the developers-portal backend; webhooks through the payouts `/v2`
// `/webhook` endpoints — both authenticated with the signed-in user's access token.

// ====== API KEYS =========================================================
// Live api-key management for the signed-in partner, per env (test = staging01, live = production).
// Listing returns metadata only; a secret is shown solely when revealed or freshly created/rotated.
function KeysView() {
  const app = useApp();
  const env: OnboardEnv = app.isLive ? 'production' : 'staging01';
  // Note: production key management is gated at the dashboard shell (ProductionAccessPending) when the
  // partner isn't operational, so this view only mounts when keys are manageable. The production `/keys`
  // routes also enforce it server-side (403 PARTNER_NOT_OPERATIONAL) as the authoritative check.

  const [keys, setKeys] = useState<ApiKeyMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revealOpen, setRevealOpen] = useState(false); // reveal drawer is open (may still be loading the secret)
  const [revealed, setRevealed] = useState<string | null>(null); // full key shown in the drawer
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyMeta | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setError('');
    app
      .getAccessToken()
      .then((token) => (token ? listKeys(token, env) : Promise.reject(new Error('Your session has expired. Please sign in again.'))))
      .then(
        (k) => {
          setKeys(k);
          setLoading(false);
        },
        (e: Error) => {
          setError(e?.message || 'Could not load keys.');
          setLoading(false);
        }
      );
  };
  useEffect(load, [env]);

  const run = async (fn: (token: string) => Promise<void>, fallback: string) => {
    setBusy(true);
    setError('');
    try {
      const token = await app.getAccessToken();
      if (!token) throw new Error('Your session has expired. Please sign in again.');
      await fn(token);
    } catch (e: any) {
      setError(e?.message || fallback);
    } finally {
      setBusy(false);
    }
  };

  const closeReveal = () => {
    setRevealOpen(false);
    setRevealed(null);
  };
  const doReveal = (k: ApiKeyMeta) => {
    // Open the drawer right away, then fetch the secret so the panel appears without waiting on the network.
    setRevealed(null);
    setRevealOpen(true);
    run(async (token) => {
      try {
        setRevealed(await revealKey(token, env, k.id));
      } catch (e) {
        setRevealOpen(false); // surface the error via the page banner instead of a stuck spinner
        throw e;
      }
    }, 'Could not reveal the key.');
  };
  const doCreate = () =>
    run(async (token) => {
      // The management API keys carry no description — only an internal keyName (not surfaced in the
      // UI). Generate a unique one per creation so each "Create key" mints a distinct key rather than
      // rotating a shared one.
      const key = await rotateKey(token, env, `key-${Date.now().toString(36)}`);
      setCreateOpen(false);
      setRevealed(key);
      setRevealOpen(true);
      load();
    }, 'Could not create the key.');
  const doExpire = (k: ApiKeyMeta) =>
    run(async (token) => {
      await expireKey(token, env, k.id);
      setRevokeTarget(null);
      load();
    }, 'Could not revoke the key.');

  const fmtDate = (s: string) => {
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString();
  };

  return (
    <div>
      <PageTitle
        title="API keys"
        sub="Secret keys authenticate your requests. Keep them server-side."
        action={
          <Button leftIcon="add" onClick={() => setCreateOpen(true)}>
            Create key
          </Button>
        }
      />
      <Banner kind="brand" style={{ marginBottom: 20 }}>
        Managing keys for <strong>{app.isLive ? 'Live (production)' : 'Test (staging)'}</strong>. Secrets are shown only when you reveal or
        create them — store them somewhere safe.
      </Banner>
      {/* Sonar publishable key: one value per partner+env (not per secret key), so it's shown once here
          rather than repeated on every row — unlike the api-keys above it, it's non-secret and safe to
          ship into the partner's own browser code to initialize the Sonar SDK client-side. Copy sits
          next to the key itself, not up in the header bar next to the "Sonar key" title. */}
      {keys[0]?.sonarKey && (
        <CodeBlock title="Sonar key" code={keys[0].sonarKey} dark={false} copyPlacement="body" style={{ marginBottom: 20 }} />
      )}
      {error && (
        <Banner kind="critical" style={{ marginBottom: 16 }}>
          {error}
        </Banner>
      )}
      {loading ? (
        <Loading label="Loading keys…" />
      ) : keys.length === 0 ? (
        <Container paddingX="xl" paddingY="xl" textAlign="center">
          <Text textStyle="body2" color="semantic.text.secondary">
            No keys yet. Create one to get started.
          </Text>
        </Container>
      ) : (
        <Table
          columns={[
            {
              key: 'maskedKey',
              label: 'Key',
              render: (r: ApiKeyMeta) => (
                <Group variant="horizontal" spacing="s" alignItems="center">
                  <Icon name="lock" size={16} color="var(--neutral-600)" />
                  <Mono size="large">{r.maskedKey ?? '—'}</Mono>
                </Group>
              ),
            },
            {
              key: 'createdAt',
              label: 'Created',
              render: (r: ApiKeyMeta) => (
                <Text textStyle="body4" color="semantic.text.secondary">
                  {fmtDate(r.createdAt)}
                </Text>
              ),
            },
            {
              key: 'status',
              label: 'Status',
              render: (r: ApiKeyMeta) =>
                r.expiredAt ? (
                  <Group variant="vertical" spacing="xxs">
                    <Pill kind="neutral">Revoked</Pill>
                    <Text textStyle="body4" color="semantic.text.secondary">
                      {fmtDate(r.expiredAt)}
                    </Text>
                  </Group>
                ) : (
                  <Pill kind="success">Active</Pill>
                ),
            },
            {
              key: 'x',
              label: '',
              align: 'right',
              render: (r: ApiKeyMeta) =>
                r.expiredAt ? null : (
                  <Group variant="horizontal" spacing="m" justifyContent="flex-end">
                    <PennyNakedButton
                      variant="primary"
                      size="medium"
                      label="Reveal"
                      aria-label={`Reveal key ${r.maskedKey ?? ''}`}
                      onClick={() => doReveal(r)}
                    />
                    <PennyNakedButton
                      variant="critical"
                      size="medium"
                      label="Revoke"
                      aria-label={`Revoke key ${r.maskedKey ?? ''}`}
                      onClick={() => setRevokeTarget(r)}
                    />
                  </Group>
                ),
            },
          ]}
          rows={keys}
        />
      )}

      {/* reveal / newly created key — the drawer already supplies the "API key" header and the
          footer, so this shows the key panel alone rather than onboarding's badge-and-heading
          treatment, and puts Done in the footer like every other drawer here. */}
      <Drawer
        open={revealOpen}
        onClose={closeReveal}
        title="API key"
        footer={revealed ? <Button onClick={closeReveal}>Done</Button> : undefined}
      >
        {revealed ? <ApiKeyPanel apiKey={revealed} /> : <Loading label="Revealing key…" />}
      </Drawer>

      {/* create */}
      <Drawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create API key"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={doCreate}>
              Create key
            </Button>
          </>
        }
      >
        <Text textStyle="body2" color="semantic.text.secondary">
          This generates a new secret API key for <strong>{app.isLive ? 'Live (production)' : 'Test (staging)'}</strong>. The full key is
          shown right after it’s created — copy it somewhere safe and keep it server-side.
        </Text>
      </Drawer>

      {/* revoke confirm (danger) */}
      <Drawer
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke key"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button variant="critical" disabled={busy} onClick={() => revokeTarget && doExpire(revokeTarget)}>
              Revoke key
            </Button>
          </>
        }
      >
        {revokeTarget && (
          <>
            <Banner kind="critical" style={{ marginBottom: 16 }}>
              Revoking <strong className="om-mono">{revokeTarget.maskedKey ?? 'this key'}</strong> disables it immediately and can’t be
              undone. Any request still using it will start failing.
            </Banner>
            <Text textStyle="body2" color="semantic.text.secondary">
              If this key is in use, <strong style={{ color: 'var(--text-primary)' }}>create a new key first</strong> and move your
              integration over to it before revoking this one.
            </Text>
          </>
        )}
      </Drawer>
    </div>
  );
}

// ====== LOGS =============================================================
function statusColor(code: number): string {
  return code < 300 ? 'success' : code < 400 ? 'informative' : code < 500 ? 'warning' : 'critical';
}

interface LogDetailProps {
  log: any;
  style?: CSSProperties;
}
function LogDetail({ log }: LogDetailProps) {
  const ok = log.status < 400;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Method m={log.method} />
        <Mono style={{ wordBreak: 'break-all' }}>{log.path}</Mono>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, color: 'var(--text-secondary)' }}>
        <Mono>● {log.status}</Mono>
        <span>{log.ms} ms</span>
        <Mono>{log.id}</Mono>
        <span>{log.time}</span>
      </div>
      <Container style={{ marginBottom: 8 }}>
        <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
          Request body
        </Text>
      </Container>
      <CodeBlock
        dark
        code={jsonStr(
          log.method === 'POST' && log.path.includes('payments')
            ? { amount: 125000, originatingAccountId: 'acct_9a2f7', receivingAccountId: 'acct_7c4b1', deductionDate: '2026-06-10' }
            : {}
        )}
        maxHeight={160}
      />
      <Container style={{ margin: '16px 0 8px' }}>
        <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
          Response
        </Text>
      </Container>
      <CodeBlock dark code={jsonStr(ok ? RES.payment : RES.paymentEligibilityError)} maxHeight={220} />
    </div>
  );
}

function LogsView() {
  const [detail, setDetail] = useState<any | null>(null);
  return (
    <div>
      <PageTitle
        title={
          <>
            Logs <PreviewBadge />
          </>
        }
        sub="Every API request, with latency and request id. Reachable from any key."
      />
      <PreviewNote>A request-log inspector needs a partner-facing log store the backend doesn't have yet.</PreviewNote>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1, maxWidth: 320 }}>
          <Input prefix="" placeholder="Filter by path, status, or request id" />
        </div>
        <Button variant="tertiary" leftIcon="filter" size="small">
          Filters
        </Button>
      </div>
      <Table
        onRow={(r) => setDetail(r)}
        columns={[
          { key: 'method', label: 'Method', render: (r) => <Method m={r.method} /> },
          {
            key: 'path',
            label: 'Path',
            render: (r) => <Mono size="caption">{r.path}</Mono>,
          },
          {
            key: 'status',
            label: 'Status',
            render: (r) => (
              <Mono bold style={{ color: `var(--${statusColor(r.status)}-700)` }}>
                {r.status}
              </Mono>
            ),
          },
          {
            key: 'ms',
            label: 'Latency',
            align: 'right',
            render: (r) => <Mono>{r.ms} ms</Mono>,
          },
          {
            key: 'time',
            label: 'Time',
            align: 'right',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.time}
              </Mono>
            ),
          },
          {
            key: 'id',
            label: 'Request id',
            align: 'right',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.id}
              </Mono>
            ),
          },
        ]}
        rows={DASH.logs}
      />
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Request log">
        {detail && <LogDetail log={detail} />}
      </Drawer>
    </div>
  );
}

// ====== EVENTS ===========================================================
interface EventDetailProps {
  evt: any;
  style?: CSSProperties;
}
function EventDetail({ evt }: EventDetailProps) {
  const ok = evt.code < 400;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Mono size="large" bold>
          {evt.type}
        </Mono>
        <Pill kind={ok ? 'success' : 'critical'}>{ok ? 'Delivered' : 'Failed'}</Pill>
      </div>
      <Card padding={18} style={{ marginBottom: 16 }}>
        <DefList
          rows={[
            ['Event id', evt.id],
            ['Related payment', evt.payment],
            ['Your endpoint', 'https://api.partner.co/webhooks/melio'],
            ['Response code', String(evt.code)],
            ['Attempts', String(evt.attempts)],
            ['Delivered at', evt.time],
          ]}
        />
      </Card>
      <Container style={{ marginBottom: 8 }}>
        <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
          Payload
        </Text>
      </Container>
      <CodeBlock
        dark
        code={jsonStr({
          id: evt.id,
          type: evt.type,
          created: '2026-05-28T09:31:48Z',
          data: { object: 'payment', id: evt.payment, status: evt.type.split('.')[1] },
        })}
        maxHeight={240}
      />
    </div>
  );
}

function EventsView() {
  const [detail, setDetail] = useState<any | null>(null);
  const [retried, setRetried] = useState<Record<string, boolean>>({});
  return (
    <div>
      <PageTitle
        title={
          <>
            Events <PreviewBadge />
          </>
        }
        sub="Webhook events with delivery status. Inspect payloads and re-trigger any event."
      />
      <PreviewNote>Events depend on payment-status webhooks, which aren't in scope yet.</PreviewNote>
      <Table
        onRow={(r) => setDetail(r)}
        columns={[
          {
            key: 'type',
            label: 'Event',
            render: (r) => <Mono bold>{r.type}</Mono>,
          },
          {
            key: 'payment',
            label: 'Resource',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.payment}
              </Mono>
            ),
          },
          {
            key: 'status',
            label: 'Delivery',
            render: (r) => (
              <Pill kind={r.code < 400 ? 'success' : 'critical'}>{r.code < 400 ? 'Delivered' : `Failed · ${r.attempts}×`}</Pill>
            ),
          },
          {
            key: 'time',
            label: 'Time',
            align: 'right',
            render: (r) => (
              <Mono size="caption" color="semantic.text.secondary">
                {r.time}
              </Mono>
            ),
          },
          {
            key: 'x',
            label: '',
            align: 'right',
            render: (r) => (
              <NakedButton
                color="var(--accent)"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  setRetried((s) => ({ ...s, [r.id]: true }));
                }}
              >
                {retried[r.id] ? 'Re-sent ✓' : 'Re-trigger'}
              </NakedButton>
            ),
          },
        ]}
        rows={DASH.events}
      />
      <Drawer open={!!detail} onClose={() => setDetail(null)} title="Event">
        {detail && <EventDetail evt={detail} />}
      </Drawer>
    </div>
  );
}

// ====== WEBHOOKS =========================================================
// Live against the payouts `/v2/webhook` endpoints (a single per-partner endpoint) authenticated
// with the signed-in user's access token. GET the endpoint, PATCH to create/update url + events +
// active state, DELETE to remove it.
const FALLBACK_EVENTS: WebhookEventName[] = [
  'api.entity.created',
  'api.entity.updated',
  'api.account.created',
  'api.account.updated',
  'api.account.deleted',
  'api.payment.created',
  'api.payment.updated',
  'api.limitation.updated',
];

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

function WebhooksView() {
  const ctx = useApiCtx();
  const hook = useAsync((signal: AbortSignal) => getWebhook(ctx, signal), [ctx.env]);
  const eventsQ = useAsync((signal: AbortSignal) => listWebhookEvents(ctx, signal), [ctx.env]);
  const available: WebhookEventName[] = eventsQ.data && eventsQ.data.length ? eventsQ.data : FALLBACK_EVENTS;
  const webhook = hook.data || null;

  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<WebhookEventName>>(new Set());
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const openEditor = () => {
    setUrl(webhook?.url || '');
    setDescription(webhook?.description || '');
    setSelected(new Set(webhook?.events || []));
    setError('');
    setEditing(true);
  };

  const toggleEvent = (name: WebhookEventName) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const allSelected = available.length > 0 && available.every((e) => selected.has(e));

  const save = async () => {
    if (!isHttpsUrl(url)) {
      setError('Enter a valid https URL.');
      return;
    }
    if (selected.size === 0) {
      setError('Subscribe to at least one event.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await patchWebhook(ctx, { url, description: description || undefined, events: [...selected] });
      setEditing(false);
      hook.reload();
    } catch (e: any) {
      setError(e?.message || 'Could not save the webhook endpoint.');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (isActive: boolean) => {
    setToggling(true);
    setError('');
    try {
      await patchWebhook(ctx, { isActive });
      hook.reload();
    } catch (e: any) {
      setError(e?.message || 'Could not update the webhook endpoint.');
    } finally {
      setToggling(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    setError('');
    try {
      await deleteWebhook(ctx);
      hook.reload();
    } catch (e: any) {
      setError(e?.message || 'Could not delete the webhook endpoint.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      <PageTitle
        title="Webhooks"
        sub="Register a single https endpoint; we POST signed events (X-Melio-Signature) when resources change."
        action={
          hook.loading ? undefined : (
            <Button leftIcon={webhook ? 'edit' : 'add'} onClick={openEditor}>
              {webhook ? 'Edit endpoint' : 'Configure endpoint'}
            </Button>
          )
        }
      />

      {error && (
        <Banner kind="critical" style={{ marginBottom: 16 }}>
          {error}
        </Banner>
      )}

      {hook.loading ? (
        <Loading label="Loading webhook endpoint…" />
      ) : hook.error ? (
        <ErrorBanner error={hook.error} onRetry={hook.reload} />
      ) : !webhook ? (
        <Card padding={32} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          No webhook endpoint configured yet. Configure one to start receiving events.
        </Card>
      ) : (
        <>
          <Card padding={22} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: webhook.isActive ? 'var(--success-100)' : 'var(--neutral-200)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="fast" size={22} color={webhook.isActive ? 'var(--success-700)' : 'var(--neutral-600)'} />
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Mono bold style={{ wordBreak: 'break-all' }}>
                  {webhook.url}
                </Mono>
                {webhook.description && (
                  <Text textStyle="body4" color="semantic.text.secondary">
                    {webhook.description}
                  </Text>
                )}
                <Text textStyle="body4" color="semantic.text.secondary">
                  {webhook.events.length} event{webhook.events.length === 1 ? '' : 's'} subscribed
                </Text>
              </div>
              <Pill kind={webhook.isActive ? 'success' : 'neutral'}>{webhook.isActive ? 'Active' : 'Paused'}</Pill>
            </div>
            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: '1px solid var(--border-static)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Text textStyle="body4" color="semantic.text.secondary" style={{ flex: 1 }}>
                {toggling
                  ? webhook.isActive
                    ? 'Pausing deliveries…'
                    : 'Enabling deliveries…'
                  : webhook.isActive
                    ? 'Deliveries are enabled.'
                    : 'Deliveries are paused.'}
              </Text>
              <Toggle value={webhook.isActive} onChange={(v: boolean) => toggleActive(v)} loading={toggling} disabled={removing} />
              <NakedButton
                color="var(--critical-700)"
                disabled={toggling || removing}
                onClick={() => {
                  if (!toggling && !removing) remove();
                }}
              >
                {removing ? 'Deleting…' : 'Delete'}
              </NakedButton>
            </div>
          </Card>
          <Container paddingBottom="s">
            <Text as="h3" textStyle="heading3Semi">
              Subscribed events
            </Text>
          </Container>
          <Card padding={8}>
            {webhook.events.length === 0 ? (
              <Container paddingX="s" paddingY="s">
                <Text textStyle="body3" color="semantic.text.secondary">
                  No events subscribed.
                </Text>
              </Container>
            ) : (
              webhook.events.map((e, i) => (
                <div
                  key={e}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderTop: i ? '1px solid var(--border-static)' : 'none',
                  }}
                >
                  <Mono style={{ flex: 1 }}>{e}</Mono>
                  <Pill kind="success">on</Pill>
                </div>
              ))
            )}
          </Card>
        </>
      )}

      <Drawer
        open={editing}
        onClose={() => setEditing(false)}
        title={webhook ? 'Edit webhook endpoint' : 'Configure webhook endpoint'}
        footer={
          <>
            <Button variant="tertiary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              Save endpoint
            </Button>
          </>
        }
      >
        {error && (
          <Banner kind="critical" style={{ marginBottom: 16 }}>
            {error}
          </Banner>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Endpoint URL" required helper="Must be https. Each delivery is signed with HMAC-SHA256.">
            <Input value={url} onChange={setUrl} placeholder="https://api.partner.co/webhooks/melio" mono />
          </Field>
          <Field label="Description" helper="Optional label to recognise this endpoint.">
            <Input value={description} onChange={setDescription} placeholder="Production endpoint" />
          </Field>
          <div>
            {/* paddingRight 20 (Card padding 8 + row padding 12) lines this toggle up with the column inside the box */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, paddingRight: 20 }}>
              <Text textStyle="body4Semi" color="semantic.text.secondary" style={{ flex: 1 }}>
                Events {eventsQ.loading ? '· loading…' : ''}
              </Text>
              {available.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <Text textStyle="caption1" color="semantic.text.secondary">
                    {allSelected ? 'Clear all' : 'Select all'}
                  </Text>
                  <Toggle value={allSelected} onChange={(v: boolean) => setSelected(v ? new Set(available) : new Set())} />
                </label>
              )}
            </div>
            <Card padding={8}>
              {available.map((e, i) => (
                <div
                  key={e}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderTop: i ? '1px solid var(--border-static)' : 'none',
                  }}
                >
                  <Mono style={{ flex: 1 }}>{e}</Mono>
                  <Toggle value={selected.has(e)} onChange={() => toggleEvent(e)} />
                </div>
              ))}
            </Card>
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export { KeysView, LogsView, LogDetail, EventsView, EventDetail, WebhooksView, statusColor };
